import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { AgeGroup, getAgeGroup } from '@repo/utils';
import { getShouldDisplaySeeding } from 'helpers';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import useFeature from 'services/features/useFeature';
import { getSortedKitsForTinyAccountId } from 'store/kits';
import { FeedbackPageType } from 'types/Feedback';
import { InsightEvaluationSection } from 'types/Insights';

import {
  AudienceFeedbackOverlay,
  ConsultWidget,
  ContainerSizes,
  DesignButton,
  FeedbackWidget,
  PageContainer,
  Typography,
} from 'components';
import AgeGroupTransitionPopup from 'components/AgeGroupTransitionPopup/AgeGroupTransitionPopup';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';
import VersionPill from 'components/VersionPill/VersionPill';
import { getHasModalOpened } from 'store/modal';
import FirstLookPopup from './FirstLook/FirstLookPopup';
import { shouldShowFirstLook } from './FirstLook/helpers';
import { PractitionerNoteAlert } from './PractitionerNoteAlert/PractitionerNoteAlert';
import { ActionPlanCard } from './SummaryCards/ActionPlanCard/ActionPlanCard';
import { ConditionMetricsGridCard } from './SummaryCards/ConditionMetricsGridCard/ConditionMetricsGridCard';
import { GutTypeCard } from './SummaryCards/GutTypeCard/GutTypeCard';
import { MatIndexCard } from './SummaryCards/MatIndexCard/MatIndexCard';
import { MicrobiomeScoreSummaryCard } from './SummaryCards/MicrobiomeScoreSummaryCard/MicrobiomeScoreSummaryCard';
import { MicrobiomeSeedingCard } from './SummaryCards/MicrobiomeSeedingCard/MicrobiomeSeedingCard';
import { NoteCard } from './SummaryCards/NoteCard/NoteCard';
import { ReplayFirstLookCard } from './SummaryCards/ReplayFirstLookCard/ReplayFirstLookCard';
import { SidebarActionCard } from './SummaryCards/SidebarActionCard/SidebarActionCard';
import { YourSampleCard } from './SummaryCards/YourSampleCard/YourSampleCard';

import { FirstLookFeatureFlags } from './FirstLook/helpers/shouldShowFirstLook';
import VersionUpdateBanner from './VersionUpdateBanner/VersionUpdateBanner';

import styles from './Summary.module.scss';

export type InsightSectionWithEvaluationGroupCounts =
  InsightEvaluationSection & {
    great: number;
    okay: number;
    support: number;
  };

export type ConditionMetricGroup = {
  id: number;
  condition: string;
  condition_display_name: string;
  good: number;
  okay: number;
  improve: number;
  support: number;
  metrics: {
    metric_id: number;
    name: string;
    evaluation_color: string;
    user_value: number;
    health_metrics_barscale_percentage: number;
  }[];
};

const visitKey = `summary_visit_count`;

const Summary = () => {
  const { currentKit, currentKitId, currentTinyAccountId } =
    useCurrentKitAndTinyAccount();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isFeatureEnabled: displayFirstLookPopup } =
    useFeature('first_look_popup');
  const { isFeatureEnabled: displayFirstLookFeedback } = useFeature(
    'first_look_feedback',
  );
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const firstLookModalIdentifier = {
    name: 'firstLook',
    props: {},
  };
  const isFirstLookDismissed = useSelector(
    getHasModalOpened(firstLookModalIdentifier),
  );

  const displaySeeding = useMemo(
    () => (currentKit ? getShouldDisplaySeeding(currentKit) : false),
    [currentKit],
  );

  const isToddler =
    currentKit?.age_at_sampling && currentKit.age_at_sampling <= 1095;

  const allKitsForAccount = useSelector(
    getSortedKitsForTinyAccountId(currentTinyAccountId),
  );

  const secondLatestKit = currentKit?.is_resample // is_resample is a way to know if there are more than two kits for an account
    ? allKitsForAccount?.[1]
    : undefined;

  const secondLatestKitAgeGroup = secondLatestKit
    ? getAgeGroup(secondLatestKit.age_at_sampling ?? 0)
    : undefined;

  const kitAgeGroup = getAgeGroup(currentKit?.age_at_sampling ?? 0);
  const transitionModalAgeGroups = [AgeGroup.TODDLER, AgeGroup.CHILD];

  const shouldShowAgeGroupTransitionPopup =
    !!currentKit?.is_resample && // No need to check for the second latest kit if there is no resample
    kitAgeGroup !== secondLatestKitAgeGroup &&
    transitionModalAgeGroups.includes(kitAgeGroup);

  const featureV2 = useFeature('first_look_v2');
  const featureV2CutoffDate = featureV2.variables?.cutoff_date;
  const featureV2Vaginal = useFeature('first_look_v2_vaginal');

  const featureFlags: FirstLookFeatureFlags = {
    v2: featureV2.isFeatureEnabled,
    v2Vaginal: featureV2Vaginal.isFeatureEnabled,
    v2OverrideDate: featureV2CutoffDate && new Date(featureV2CutoffDate),
  };

  const shouldShowFirstLookPopup = useMemo(
    () =>
      displayFirstLookPopup && shouldShowFirstLook(currentKit, featureFlags),
    [displayFirstLookPopup, currentKit, featureFlags],
  );

  useEffect(() => {
    if (!currentKitId || !isFirstLookDismissed) return;

    const currentVisitCount = parseInt(
      sessionStorage.getItem(visitKey) || '0',
      10,
    );
    const newVisitCount = currentVisitCount + 1;
    sessionStorage.setItem(visitKey, newVisitCount.toString());

    if (newVisitCount < 2) return;

    const t = window.setTimeout(() => {
      setIsFeedbackOpen(true);
    }, 3000);

    return () => {
      window.clearTimeout(t);
    };
  }, [currentKitId, isFirstLookDismissed]);

  if (!currentKitId) return <></>;

  return (
    <PageContainer size={ContainerSizes.LG}>
      {displayFirstLookFeedback ? (
        <AudienceFeedbackOverlay
          kitId={currentKitId}
          isOpen={isFeedbackOpen}
          onOpenChange={setIsFeedbackOpen}
          feedbackType='results_summary'
          questions={['NpsQuestion', 'NpsTextQuestion']}
        />
      ) : null}
      <VersionUpdateBanner />
      <PractitionerNoteAlert />
      <TwoColumnGrid>
        {shouldShowAgeGroupTransitionPopup ? (
          <AgeGroupTransitionPopup
            newAgeGroup={kitAgeGroup as 'toddler' | 'child'}
          />
        ) : null}
        {shouldShowFirstLookPopup ? <FirstLookPopup /> : null}

        <TwoColumnGrid.Wide>
          <div className='flex flex-column gap-4'>
            <MicrobiomeScoreSummaryCard />
            <ConsultWidget title='Need help understanding your results?' />
            {shouldShowFirstLookPopup ? <ReplayFirstLookCard /> : null}
            <NoteCard />
            <YourSampleCard />
            <ConditionMetricsGridCard />
            <ActionPlanCard />

            <div className='hide-on-mobile'>
              <FeedbackWidget
                kitId={currentKitId}
                pageType={FeedbackPageType.SUMMARY}
                pageId={0}
              />
            </div>
          </div>
        </TwoColumnGrid.Wide>
        <TwoColumnGrid.Slim isSticky>
          <div className={styles.slimSection}>
            {isToddler ? (
              <>
                <MatIndexCard />
                {displaySeeding ? <MicrobiomeSeedingCard /> : null}
                <GutTypeCard />
              </>
            ) : (
              <>
                <SidebarActionCard />
                {displaySeeding ? <MicrobiomeSeedingCard /> : null}
                <GutTypeCard />
              </>
            )}

            <div className='hide-on-desktop'>
              <FeedbackWidget
                kitId={currentKitId}
                pageType={FeedbackPageType.SUMMARY}
                pageId={0}
              />
            </div>
            <div className='hide-on-mobile'>
              <DesignButton
                color='purple'
                width='full'
                onClick={() => {
                  navigate(
                    `${pathname}/insights/all-insights?evaluations=red%2Corange%2Cgreen%2Cgrey`,
                    {
                      state: { from: pathname },
                    },
                  );
                }}
              >
                See all metrics
              </DesignButton>
            </div>
          </div>
        </TwoColumnGrid.Slim>
      </TwoColumnGrid>
      {currentKit?.sci_initial_version && (
        <div className='hide-on-desktop'>
          <div className='flex gap-2'>
            <Typography variant='label' className='my-auto'>
              System version
            </Typography>
            <VersionPill />
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default Summary;
